import * as React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { matchPath, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Close as CloseIcon, ImageOutlined } from "@/components/icons";
import {
  FEEDBACK_CATEGORIES,
  feedbackErrorKey,
  useSubmitFeedback,
  type FeedbackCategory,
  type FeedbackConfig,
} from "@/api/feedback";
import { DEMO_SESSION_ID } from "@/api/demoSession";
import { useAppVersion, useMe } from "@/api/hooks";
import { loadedBuild } from "@/api/loadedBuild";
import { formatNumber } from "@/i18n/format";

// What the server re-encodes (``app.services.brand.images``): PNG, JPEG
// and WebP. SVG is a document that can carry script, never a screenshot.
const SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const DEFAULT_MAX_MESSAGE = 5000;
const DEFAULT_MAX_SCREENSHOT = 10 * 1024 * 1024;
const MB = 1024 * 1024;

/** The session the user is looking at, from the route. */
export function sessionIdFromPath(pathname: string): string | null {
  const match = matchPath("/app/sessions/:sessionId", pathname);
  const id = match?.params.sessionId ?? null;
  // ``archived`` and the demo session (FW-34, not a session row) share
  // the pattern but aren't sessions.
  return id && id !== "archived" && id !== DEMO_SESSION_ID ? id : null;
}

export type FeedbackFormProps = {
  config: FeedbackConfig;
  /** ``null``: the user picks bug, idea or other (the dialog). A fixed
   *  category hides the picker and the intro (the Ask panel). */
  category: FeedbackCategory | null;
  initialMessage?: string;
  source?: "ask_panel";
  onSent: (id: string) => void;
  onCancel?: () => void;
  onMessageChange?: (text: string) => void;
  /** Under the message field: the Ask panel's FW matches. */
  children?: React.ReactNode;
};

/** The feedback form: a category (unless fixed), a message, an optional
 *  screenshot, and — shown before sending — the context that goes with
 *  it (who, which organisation, which page and session, the app version,
 *  the browser and the UI language). The dialog and the Ask panel both
 *  render it; a request and a feedback note are one row. */
export function FeedbackForm(props: FeedbackFormProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const me = useMe();
  const version = useAppVersion();
  const submit = useSubmitFeedback();
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const [picked, setPicked] = React.useState<FeedbackCategory>("bug");
  const category = props.category ?? picked;
  const [message, setMessage] = React.useState(props.initialMessage ?? "");
  const [screenshot, setScreenshot] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const changeMessage = (text: string) => {
    setMessage(text);
    props.onMessageChange?.(text);
  };

  const maxMessage = props.config.max_message_chars || DEFAULT_MAX_MESSAGE;
  const maxScreenshot = props.config.max_screenshot_bytes || DEFAULT_MAX_SCREENSHOT;
  const route = `${location.pathname}${location.search}`;
  const sessionId = sessionIdFromPath(location.pathname);
  const appVersion = loadedBuild()?.version ?? version.data?.version ?? null;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const uiLanguage = i18n.language;
  const user = me.data?.user;
  const trimmed = message.trim();
  const tooLong = trimmed.length > maxMessage;
  // A refused file is never kept, so it can't block sending: the warning
  // says so and the feedback goes without it.
  const canSend = trimmed.length > 0 && !tooLong && !submit.isPending;

  const onPick = (files: FileList | null) => {
    const next = files?.[0] ?? null;
    if (fileRef.current) fileRef.current.value = "";
    if (!next) return;
    if (!(SCREENSHOT_TYPES as readonly string[]).includes(next.type)) {
      setScreenshot(null);
      setFileError(t("feedback.errors.screenshot_type_unsupported"));
      return;
    }
    if (next.size > maxScreenshot) {
      setScreenshot(null);
      setFileError(
        t("feedback.errors.screenshot_too_large_limit", {
          size: formatNumber(Math.round(maxScreenshot / MB)),
        }),
      );
      return;
    }
    setFileError(null);
    setScreenshot(next);
  };

  const onSend = async () => {
    if (!canSend) return;
    setError(null);
    try {
      const created = await submit.mutateAsync({
        category,
        message: trimmed,
        orgSlug: props.config.org_slug,
        route,
        sessionId,
        appVersion,
        uiLanguage,
        screenshot,
        source: props.source,
      });
      props.onSent(created.id);
    } catch (err: unknown) {
      const key = feedbackErrorKey(err);
      setError(key ? t(key) : t("feedback.errors.generic"));
    }
  };

  const none = t("feedback.context.none");
  const contextRows: { label: string; value: string }[] = [
    {
      label: t("feedback.context.user"),
      value: user ? `${user.display_name} (${user.email})` : none,
    },
    {
      label: t("feedback.context.org"),
      value: props.config.org_name ?? props.config.org_slug ?? none,
    },
    { label: t("feedback.context.page"), value: route },
    { label: t("feedback.context.session"), value: sessionId ?? none },
    { label: t("feedback.context.version"), value: appVersion ?? none },
    { label: t("feedback.context.browser"), value: userAgent || none },
    { label: t("feedback.context.language"), value: uiLanguage },
  ];

  return (
    <Stack sx={{ gap: 2, pt: 0.5 }}>
      {props.category === null && (
        <Typography variant="body2" color="text.secondary">
          {t("feedback.intro")}
        </Typography>
      )}
      {props.category === null && (
        <Box>
          <Typography variant="subtitle2" component="p" id="feedback-category-label" sx={{ mb: 0.75 }}>
            {t("feedback.category_label")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={category}
            onChange={(_e, next: FeedbackCategory | null) => {
              if (next) setPicked(next);
            }}
            aria-labelledby="feedback-category-label"
          >
            {FEEDBACK_CATEGORIES.map((c) => (
              <ToggleButton key={c} value={c}>
                {t(`feedback.category.${c}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      <TextField
        label={t("feedback.message_label")}
        placeholder={t(`feedback.message_placeholder.${category}`)}
        value={message}
        onChange={(e) => changeMessage(e.target.value)}
        multiline
        minRows={5}
        maxRows={14}
        required
        autoFocus
        error={tooLong}
        helperText={t("feedback.message_count", {
          used: formatNumber(trimmed.length),
          max: formatNumber(maxMessage),
        })}
      />
      {props.children}
      <Box>
        <input
          ref={fileRef}
          type="file"
          accept={SCREENSHOT_TYPES.join(",")}
          hidden
          data-testid="feedback-screenshot-input"
          onChange={(e) => onPick(e.target.files)}
        />
        {screenshot ? (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <ImageOutlined fontSize="small" color="action" />
            <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {screenshot.name}
            </Typography>
            <Tooltip title={t("feedback.screenshot_remove")}>
              <IconButton
                size="small"
                aria-label={t("feedback.screenshot_remove")}
                onClick={() => setScreenshot(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ImageOutlined />}
            onClick={() => fileRef.current?.click()}
          >
            {t("feedback.screenshot_add")}
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {t("feedback.screenshot_hint", {
            size: formatNumber(Math.round(maxScreenshot / MB)),
          })}
        </Typography>
        {fileError && (
          <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setFileError(null)}>
            {fileError}
          </Alert>
        )}
      </Box>
      <Box
        component="section"
        aria-labelledby="feedback-context-heading"
        sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}
      >
        <Typography variant="subtitle2" id="feedback-context-heading" sx={{ mb: 0.5 }}>
          {t("feedback.context.heading")}
        </Typography>
        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: "max-content minmax(0, 1fr)",
            columnGap: 1.5,
            rowGap: 0.25,
            fontSize: "0.8125rem",
          }}
        >
          {contextRows.map((row) => (
            <React.Fragment key={row.label}>
              <Box component="dt" sx={{ color: "text.secondary" }}>
                {row.label}
              </Box>
              <Box component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>
                {row.value}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1 }}>
        {props.onCancel && <Button onClick={props.onCancel}>{t("feedback.cancel")}</Button>}
        <Button variant="contained" onClick={() => void onSend()} disabled={!canSend}>
          {submit.isPending ? t("feedback.sending") : t("feedback.send")}
        </Button>
      </Stack>
    </Stack>
  );
}
